/**
 * Inferred-rules card (post-import save prompt).
 *
 * Rendered after an `ingest_bank_statement` tool result when the server's
 * D.1 historical-memory layer matched ≥1 narration to a ledger inferred
 * from the tenant's last 3 months of Tally vouchers. Surfaces:
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │ 📚  Learned 47 narration→ledger mappings from your last 3 months │
 *   │     of Tally (1,243 vouchers scanned).                           │
 *   │                                                                  │
 *   │  Save these so future imports map automatically?                 │
 *   │                                                                  │
 *   │  [Save all]   [Review individually]   [Discard]                  │
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * "Save all" → calls appendInferredRules() and merges into mapping_config.csv
 * "Review individually" → opens MappingRulesEditor pre-populated with the new
 *   rules so the user can accept/edit each one
 * "Discard" → dismisses the card; rules are not persisted (next upload will
 *   re-infer from history)
 */
import { useState } from 'react';
import { BookOpen, Check, ListChecks, X, Loader2, AlertCircle } from 'lucide-react';
import clsx from 'clsx';

import type { InferredRule } from '@/types/chat';
import { appendInferredRules } from '@/lib/aiaccountant';

export interface InferredRulesCardProps {
  rules: InferredRule[];
  companyId?: string;
  historyVouchersScanned?: number;
  /** Optional handler so parent can open the MappingRulesEditor with these
   *  rules pre-populated. If not provided, the "Review individually" button
   *  falls back to "Save all" behavior. */
  onReview?: (rules: InferredRule[]) => void;
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error' | 'dismissed';

export function InferredRulesCard({
  rules,
  companyId,
  historyVouchersScanned = 0,
  onReview,
}: InferredRulesCardProps) {
  const [state, setState] = useState<SaveState>('idle');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [appendedCount, setAppendedCount] = useState<number>(0);
  const [expanded, setExpanded] = useState(false);

  if (!rules.length || state === 'dismissed') return null;

  const handleSaveAll = async () => {
    if (!companyId) {
      setErrMsg('No company ID set — open Settings and select a company first.');
      setState('error');
      return;
    }
    setState('saving');
    setErrMsg(null);
    try {
      const appended = await appendInferredRules(
        companyId,
        rules.map((r) => ({ narration_pattern: r.narration_pattern, ledger: r.ledger })),
      );
      setAppendedCount(appended);
      setState('saved');
    } catch (e) {
      setErrMsg(e instanceof Error ? e.message : 'Failed to save rules');
      setState('error');
    }
  };

  const handleReview = () => {
    if (onReview) {
      onReview(rules);
    } else {
      // Fallback if parent didn't wire onReview — same as Save all
      void handleSaveAll();
    }
  };

  const handleDiscard = () => setState('dismissed');

  return (
    <div className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm">
      <div className="flex items-start gap-2.5">
        <BookOpen className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-foreground">
            Learned {rules.length} narration→ledger {rules.length === 1 ? 'mapping' : 'mappings'} from your Tally history
          </div>
          {historyVouchersScanned > 0 && (
            <div className="text-xs text-muted-foreground mt-0.5">
              {historyVouchersScanned} vouchers scanned from the last 3 months
            </div>
          )}

          {state === 'idle' && (
            <>
              <div className="text-xs text-muted-foreground mt-1.5">
                Save these so future imports map automatically?
              </div>
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={handleSaveAll}
                  className="inline-flex items-center gap-1 rounded bg-emerald-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-600 transition-colors"
                >
                  <Check className="h-3 w-3" />
                  Save all
                </button>
                <button
                  type="button"
                  onClick={handleReview}
                  className="inline-flex items-center gap-1 rounded border border-border bg-background px-2.5 py-1 text-xs hover:bg-muted transition-colors"
                >
                  <ListChecks className="h-3 w-3" />
                  Review individually
                </button>
                <button
                  type="button"
                  onClick={handleDiscard}
                  className="inline-flex items-center gap-1 rounded border border-border bg-background px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted transition-colors"
                >
                  <X className="h-3 w-3" />
                  Discard
                </button>
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors ml-auto"
                >
                  {expanded ? 'Hide details' : 'Preview rules'}
                </button>
              </div>
            </>
          )}

          {state === 'saving' && (
            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Saving rules to your company config…
            </div>
          )}

          {state === 'saved' && (
            <div className="mt-2 flex items-center gap-2 text-xs text-emerald-600">
              <Check className="h-3.5 w-3.5" />
              Saved {appendedCount} {appendedCount === 1 ? 'rule' : 'rules'}. Future imports will use them.
            </div>
          )}

          {state === 'error' && (
            <div className="mt-2 space-y-1.5">
              <div className="flex items-center gap-2 text-xs text-destructive">
                <AlertCircle className="h-3.5 w-3.5" />
                {errMsg}
              </div>
              <button
                type="button"
                onClick={handleSaveAll}
                className="rounded border border-border bg-background px-2 py-0.5 text-xs hover:bg-muted transition-colors"
              >
                Retry
              </button>
            </div>
          )}

          {expanded && state === 'idle' && (
            <div className="mt-2.5 max-h-52 overflow-y-auto rounded border border-border bg-background">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1 text-left font-medium">Pattern</th>
                    <th className="px-2 py-1 text-left font-medium">Ledger</th>
                    <th className="px-2 py-1 text-right font-medium">Conf.</th>
                    <th className="px-2 py-1 text-left font-medium">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.slice(0, 200).map((r, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-2 py-1 truncate max-w-[200px]" title={r.sample_narration}>
                        {r.narration_pattern}
                      </td>
                      <td className="px-2 py-1 truncate max-w-[160px]" title={r.ledger}>
                        {r.ledger}
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">
                        {r.confidence}%
                      </td>
                      <td className="px-2 py-1 text-muted-foreground">
                        <span
                          className={clsx(
                            'inline-block rounded px-1 py-0.5 text-[10px]',
                            r.source === 'historical_exact'
                              ? 'bg-emerald-500/10 text-emerald-700'
                              : 'bg-amber-500/10 text-amber-700',
                          )}
                        >
                          {r.source === 'historical_exact' ? 'exact' : 'fuzzy'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {rules.length > 200 && (
                    <tr>
                      <td colSpan={4} className="px-2 py-1.5 text-center text-muted-foreground">
                        … and {rules.length - 200} more
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
